import { type BaseSafetyEventsRequest, type SafetyEventsReportRequest, SafetyEventsRequester, type SafetyEventsResponse } from '@cobliteam/api-lib';
import { Environment } from '@cobliteam/react-environments';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getSafetyEventsRequester = () =>
  new SafetyEventsRequester({
    host: env.apiHost,
  });

const QUERY_KEY = 'SAFETY_EVENTS';

const getNextPageParam = (lastPage: SafetyEventsResponse, allPages: SafetyEventsResponse[]) => {
  const loadedItems = allPages
    .flat()
    .map((response) => response.rows.length)
    .reduce((acc, a) => acc + a);

  if (lastPage.count === loadedItems) return undefined;
  return allPages.flat().length;
};

const useGetSafetyEvents = (request: BaseSafetyEventsRequest) => {
  return useInfiniteQuery<SafetyEventsResponse>({
    initialPageParam: 0,
    queryKey: [QUERY_KEY, 'ROWS', JSON.stringify(request)],
    queryFn: ({ pageParam = 0 }) =>
      getSafetyEventsRequester().getSafetyEvents({
        ...request,
        page: pageParam as number,
        size: 100,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useRequestSafetyEventsReport = () =>
  useMutation<string, unknown, { request: SafetyEventsReportRequest }>({
    mutationFn: (variables) => getSafetyEventsRequester().requestSafetyEventsReport(variables.request),
  });

export { useGetSafetyEvents, useRequestSafetyEventsReport };
